import { useEffect, useState } from "react";
import type { FC } from "react";
import { Table, Pagination, TableHead, TableHeadCell, TableBody, TableRow, TableCell } from "flowbite-react";
import { supabase, TABLES } from "../../lib/supabase";

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  floor: string;
  notes: string;
  interest_level: string;
  created_at: string;
}

interface AddressCustomerTablesProps {
  addressId?: number;
}

const AddressCustomerTables: FC<AddressCustomerTablesProps> = ({
  addressId,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<Record<string, number>>({});
  const [itemsPerPage] = useState(5); // Default to 5 items per page

  // Defines the mapping between database values and display values, and the order of display
  const customerStateMappings = [
    { dbValue: "Nicht geöffnet", displayValue: "Nicht geöffnet" }, // Assuming this might be a direct value or for new/uncontacted
    { dbValue: "nicht angetroffen", displayValue: "Nicht angetroffen" },
    { dbValue: "Rückruf", displayValue: "Rückruf" }, // Assuming this might be a direct value
    { dbValue: "kein interesse", displayValue: "Nicht interessiert" },
    { dbValue: "Nicht interessiert", displayValue: "Nicht interessiert" },
    { dbValue: "Contract", displayValue: "Auftrag" },
  ];

  useEffect(() => {
    console.log(
      "AddressCustomerTables: useEffect triggered. addressId:",
      addressId
    );

    if (!addressId) {
      setCustomers([]);
      setLoading(false);
      setError(null);
      console.log(
        "AddressCustomerTables: No addressId provided, component will handle default state or return null."
      );
      return;
    }

    const fetchCustomers = async () => {
      // addressId is guaranteed to be truthy here
      setLoading(true);
      setError(null);
      setCustomers([]); // Clear previous customers when addressId changes or fetching new

      try {
        console.log(
          `AddressCustomerTables: Fetching customers for address_id: ${addressId}`
        );
        const { data, error, status, count } = await supabase
          .from(TABLES.CUSTOMERS)
          .select("*", { count: "exact" }) // Added count for debugging
          .eq("address_id", addressId)
          .order("created_at", { ascending: false });

        console.log("AddressCustomerTables: Supabase query response", {
          data,
          error,
          status,
          count,
        });

        if (error) {
          console.error("AddressCustomerTables: Supabase query error:", error);
          throw error;
        }

        // Log unique interest levels from fetched data
        if (data) {
          const uniqueInterestLevels = [
            ...new Set(data.map((c) => c.interest_level)),
          ];
          console.log(
            "AddressCustomerTables: Unique interest_level values in fetched data:",
            uniqueInterestLevels
          );
          console.log(
            "AddressCustomerTables: Defined customerStateMappings for comparison:",
            customerStateMappings.map((m) => m.dbValue)
          );
        }

        setCustomers(data || []);
      } catch (err) {
        console.error(
          "AddressCustomerTables: Error in fetchCustomers catch block:",
          err
        );
        setError(
          err instanceof Error ? err.message : "Failed to fetch customers"
        );
        // Customers already cleared at the start of fetchCustomers
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [addressId]);

  if (!addressId) return null;
  if (loading) return <div>Kunden werden geladen...</div>;
  if (error) return <div>Fehler beim Laden der Kunden: {error}</div>;

  return (
    <div className="mt-8 space-y-8">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        An dieser Adresse{/* Kunden an dieser Adresse */}
      </h2>

      {customers.length === 0 ? (
        <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <p className="text-center text-gray-500 dark:text-gray-400">
            Diese Adresse ist leer
            {/* Keine Kunden an dieser Adresse gefunden. */}
          </p>
        </div>
      ) : (
        customerStateMappings.map((mapping) => {
          const stateCustomers = customers.filter(
            (customer) => customer.interest_level === mapping.dbValue
          );

          if (stateCustomers.length === 0) return null;

          // Sort customers by floor: "EG" first, then numeric, then others
          const parseFloor = (floorStr: string): number => {
            if (
              typeof floorStr === "string" &&
              floorStr.toUpperCase() === "EG"
            ) {
              return -1; // "EG" comes before 0 and positive numbers
            }
            const num = parseInt(floorStr, 10);
            // If floorStr is not "EG" and not a valid number (e.g., "random text", ""),
            // it will be NaN. Treat these as "highest" to sort them last.
            return isNaN(num) ? Infinity : num;
          };

          stateCustomers.sort((a, b) => {
            const floorAVal = parseFloor(a.floor);
            const floorBVal = parseFloor(b.floor);
            return floorAVal - floorBVal;
          });

          // Get current page for this state, using displayValue as the key
          const page = currentPage[mapping.displayValue] || 1;

          // Calculate pagination indexes
          const indexOfLastItem = page * itemsPerPage;
          const indexOfFirstItem = indexOfLastItem - itemsPerPage;
          const currentItems = stateCustomers.slice(
            indexOfFirstItem,
            indexOfLastItem
          );
          const totalPages = Math.ceil(stateCustomers.length / itemsPerPage);

          return (
            <div
              key={mapping.displayValue}
              className="rounded-lg bg-white p-4 shadow dark:bg-gray-800"
            >
              <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                {mapping.displayValue} ({stateCustomers.length})
              </h3>
              <Table>
                <TableHead>
                  <TableHeadCell>Name</TableHeadCell>
                  <TableHeadCell>Etage</TableHeadCell>
                  <TableHeadCell>Notizen</TableHeadCell>
                  <TableHeadCell>Datum</TableHeadCell>
                </TableHead>
                <TableBody className="divide-y">
                  {currentItems.map((customer) => (
                    <TableRow
                      key={customer.id}
                      className="bg-white dark:border-gray-700 dark:bg-gray-800"
                    >
                      <TableCell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                        {customer.first_name} {customer.last_name}
                      </TableCell>
                      <TableCell>{customer.floor}</TableCell>
                      <TableCell>{customer.notes}</TableCell>
                      <TableCell>
                        {new Date(customer.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={(newPage) =>
                      setCurrentPage((prev) => ({
                        ...prev,
                        [mapping.displayValue]: newPage,
                      }))
                    }
                    showIcons
                  />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default AddressCustomerTables;
